import { DateTime } from 'luxon';
import { useMutation } from 'urql';
import { EventClockFragment } from '@/components/Event/EventClock';
import { calculateElapsedMs } from '@/components/Event/EventClock/EventClock.utils';
import Button from '@/components/UI/Button';
import Icon from '@/components/Unistyles/Icon';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
export const EventScorekeepingPanelTimerPlayPauseFragment =
  graphql(/* GraphQL */ `
    fragment EventScorekeepingPanelTimerPlayPause on event {
      id
      event_clock {
        event_id
        is_running
        elapsed_segment_ms
        start_datetime
        event_segment {
          id
          duration_offset
          is_duration_capped
          duration
        }
      }
      event_segment_group {
        id
        direction
      }
    }
  `);
const UpsertEventClockPlayPause = graphql(/* GraphQL */ `
  mutation UpsertEventClockPlayPause(
    $eventId: uuid!
    $isRunning: Boolean!
    $elapsedSegmentMs: Int!
    $startDatetime: timestamptz!
    $eventSegmentId: uuid
  ) {
    insert_event_clock_one(
      object: {
        is_running: $isRunning
        elapsed_segment_ms: $elapsedSegmentMs
        start_datetime: $startDatetime
        event_segment_id: $eventSegmentId
        event_id: $eventId
      }
      on_conflict: {
        constraint: event_clock_pkey
        update_columns: [is_running, elapsed_segment_ms, start_datetime]
      }
    ) {
      event_id
      is_running
      elapsed_segment_ms
      start_datetime
      event {
        ...EventClock
        id
        status
      }
      event_segment {
        id
        duration_offset
      }
    }
  }
`, [EventClockFragment]);
export default function EventScorekeepingPanelTimerPlayPause({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelTimerPlayPauseFragment>
    | null
    | undefined;
}) {
  const event = readFragment(
    EventScorekeepingPanelTimerPlayPauseFragment,
    fragment,
  );
  const [, upsertEventClockPlayPause] = useMutation(UpsertEventClockPlayPause);
  const handlePlayPause = async () => {
    if (!fragment) {
      return;
    }
    const eventData = readFragment(
      EventScorekeepingPanelTimerPlayPauseFragment,
      fragment,
    );
    const startDatetimeFromFragment = eventData?.event_clock?.start_datetime;
    const elapsedSegmentMsFromFragment =
      eventData?.event_clock?.elapsed_segment_ms ?? 0;
    const currentIsRunning = eventData?.event_clock?.is_running ?? false;
    const elapsedMsToStore = calculateElapsedMs({
      isRunning: currentIsRunning,
      startDatetime: startDatetimeFromFragment ?? null,
      elapsedSegmentMs: elapsedSegmentMsFromFragment,
      isDurationCapped:
        eventData?.event_clock?.event_segment?.is_duration_capped ?? false,
      duration: eventData?.event_clock?.event_segment?.duration ?? 0,
    });
    const newIsClockRunning = !currentIsRunning;
    const newStartDatetime = DateTime.now().toISO();
    if (!newStartDatetime || !eventData) {
      return;
    }
    await upsertEventClockPlayPause({
      eventId: eventData.id,
      isRunning: newIsClockRunning,
      elapsedSegmentMs: elapsedMsToStore,
      startDatetime: newStartDatetime,
      eventSegmentId: eventData.event_clock?.event_segment?.id,
    });
  };
  return (
    <Button
      testID="ScorekeepingTimerPlayPauseButton"
      paddingHorizontal={16}
      gap={8}
      size="xl"
      variant={event?.event_clock?.is_running ? 'negative' : 'positive'}
      onPress={handlePlayPause}
    >
      <Icon
        variant="white"
        size="3xl"
        name={
          event?.event_clock?.is_running
            ? 'pause-circle-outline'
            : 'play-circle-outline'
        }
      />
      {!event?.event_clock ? (
        <Button.Text size="xl">Start Game</Button.Text>
      ) : null}
    </Button>
  );
}
